import { Composable, Inject, Setup, Vue } from '@natureos/vue-facing-decorator';

import { InFlightCell } from '@/common/helpers';
import type { ApiResponse, ApiService } from '@/common/services/api.service-new';
import type { MultiPolygonCoords } from '../hansen/hansen.types';
import { useFirelossStore } from './fireloss.store';
import type { FireLossResponse, FirelossCertaintyLayersResponse } from './fireloss.types';

/**
 * Service for the fire-driven forest-loss endpoints (v2):
 *
 *  - `getFirelossCertaintyLayers()` — singleton nested tile-URL map (per
 *    certainty → per year), cache-first with in-flight dedup.
 *  - `getFireLoss(coords, threshold)` — per-polygon stats matrix; no
 *    service-layer cache (the caller caches by fingerprint).
 */
@Composable()
export class useFirelossService extends Vue {
  @Inject()
  private readonly api!: ApiService;

  @Setup(() => useFirelossStore())
  public readonly firelossStore!: ReturnType<typeof useFirelossStore>;

  // Singleton certainty-layers resource — concurrent first-toggles join one fetch.
  private readonly certaintyLayersInFlight = new InFlightCell<ApiResponse<FirelossCertaintyLayersResponse>>();

  public async getFirelossCertaintyLayers(): Promise<ApiResponse<FirelossCertaintyLayersResponse>> {
    if (this.firelossStore.certaintyLayers) {
      return this.firelossStore.certaintyLayers;
    }
    return this.certaintyLayersInFlight.runOnce(async () => {
      const res = await this.api.get<FirelossCertaintyLayersResponse>(
        `${import.meta.env.VITE_GEE_API}/fireloss/certainty-layers`,
      );
      if (res.isSuccess && res.data) {
        this.firelossStore.setCertaintyLayers(res);
      }
      return res;
    });
  }

  /**
   * POST /fireloss/loss — fire-driven forest-loss statistics for the AOI as a
   * (year × certainty) matrix. Body identical to /hansen/forestloss
   * (`{ data, threshold }`); pass HANSEN_FOREST_THRESHOLD so `fire_vs_other.total`
   * reconciles with the Deforestation tool and the certainty map tiles.
   */
  public async getFireLoss(
    coords: MultiPolygonCoords,
    threshold: number,
  ): Promise<ApiResponse<FireLossResponse>> {
    return this.api.post<FireLossResponse>(
      `${import.meta.env.VITE_GEE_API}/fireloss/loss`,
      { data: coords, threshold },
    );
  }
}
